import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, Headphones, AlertCircle } from 'lucide-react';
import { PlanCheckout } from './PlanCheckout';
import { AudioPlayerModal } from './AudioPlayerModal';
import { AudioTrimEditor } from './AudioTrimEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const errText = (e, f) => (typeof e.response?.data?.detail === 'string' ? e.response.data.detail : f);
const hours = (min) => (min >= 60 ? `${Math.floor(min / 60)}h${String(min % 60).padStart(2, '0')}` : `${min} min`);

const Quote = ({ q, onPay }) => (
  <div data-testid="audiobook-quote">
    <p style={{ color: '#4a5568', fontSize: '0.9rem', marginBottom: '0.9rem' }}>
      Transformamos o texto do PDF em áudio narrado por voz de IA (OpenAI). O valor é calculado pelo tamanho do texto e cobrado antes da geração.
    </p>
    <dl style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.4rem 1rem', fontSize: '0.9rem', marginBottom: '1rem' }}>
      <dt>Caracteres do livro</dt><dd data-testid="audiobook-chars" style={{ fontWeight: 600 }}>{q.chars.toLocaleString('pt-BR')}</dd>
      <dt>Duração estimada</dt><dd style={{ fontWeight: 600 }}>{hours(q.minutes || Math.round(q.chars / 900))}</dd>
      <dt>Cotação do dólar (PTAX)</dt><dd style={{ fontWeight: 600 }}>{BRL(q.rate)}</dd>
      <dt style={{ fontWeight: 700 }}>Total a pagar</dt><dd data-testid="audiobook-price" style={{ fontWeight: 800, fontSize: '1.1rem' }}>{BRL(q.price)}</dd>
    </dl>
    <p style={{ fontSize: '0.78rem', color: '#718096', marginBottom: '1rem' }}>Inclui custo da IA + 15% de serviço (mínimo de R$ 5,00). Orçamento válido por 24h. A editora declara ter os direitos da versão em áudio.</p>
    <button onClick={onPay} data-testid="audiobook-pay-btn" style={{ width: '100%', background: '#2f855a', color: 'white', border: 'none', borderRadius: 8, padding: '0.75rem', fontWeight: 700, cursor: 'pointer' }}>
      Pagar e gerar audiolivro
    </button>
  </div>
);

// Editor flow: quote -> pay (Pix/card) -> background generation -> ready (preview player)
export const AudiobookModal = ({ book, token, onClose }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [q, setQ] = useState(null);
  const [err, setErr] = useState('');
  const [paying, setPaying] = useState(false);
  const [listen, setListen] = useState(false);
  const load = () => axios.get(`${API}/digital/books/${book.id}/audiobook`, { headers }).then(({ data }) => setQ(data)).catch((e) => setErr(errText(e, 'Não foi possível calcular o valor.')));
  useEffect(() => { load(); }, [book.id]);
  useEffect(() => {
    if (!['paid', 'processing'].includes(q?.status)) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [q?.status]);
  const retry = () => axios.post(`${API}/digital/books/${book.id}/audiobook/retry`, {}, { headers }).then(load).catch((e) => setErr(errText(e, 'Erro ao retomar.')));
  const st = q?.status;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="audiobook-modal" style={{ background: 'white', borderRadius: 14, width: '100%', maxWidth: 480, maxHeight: '92vh', overflowY: 'auto', padding: '1.25rem 1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: '#1a202c' }}><Headphones className="w-5 h-5" />Audiolivro</h3>
          <button onClick={onClose} data-testid="audiobook-close" style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X className="w-5 h-5" /></button>
        </div>
        <p style={{ fontWeight: 600, color: '#2D3748', marginBottom: '0.75rem' }}>{book.titulo}</p>
        {err ? <p data-testid="audiobook-error" style={{ color: '#c53030', display: 'flex', gap: 6 }}><AlertCircle className="w-4 h-4 mt-0.5" />{err}</p>
          : !q ? <p style={{ textAlign: 'center' }}><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Calculando o valor pelo texto do livro...</p>
          : st === 'quoted' && !paying ? <Quote q={q} onPay={() => setPaying(true)} />
          : st === 'quoted' ? (
            <>
              <button onClick={() => setPaying(false)} data-testid="audiobook-back-btn" style={{ background: 'none', border: 'none', color: '#4a5568', cursor: 'pointer', fontSize: '0.85rem', marginBottom: '0.5rem' }}>← Voltar ao orçamento</button>
              <PlanCheckout token={token} audio={{ bookId: book.id, price: q.price }} onPaid={load} onDone={load} />
            </>
          )
          : ['paid', 'processing'].includes(st) ? (
            <div data-testid="audiobook-progress" style={{ textAlign: 'center', padding: '0.5rem 0' }}>
              <Loader2 className="w-6 h-6 animate-spin" style={{ margin: '0 auto 0.5rem' }} />
              <p style={{ fontWeight: 600 }}>Gerando o áudio... {q.progress || 0}%</p>
              <div style={{ height: 8, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden', margin: '0.75rem 0' }}><div style={{ width: `${q.progress || 0}%`, height: '100%', background: '#2f855a', transition: 'width .4s ease' }} /></div>
              <p style={{ fontSize: '0.82rem', color: '#718096' }}>Pode fechar esta janela: a geração continua e o áudio aparece no catálogo quando ficar pronto.</p>
            </div>
          ) : st === 'failed' ? (
            <div data-testid="audiobook-failed">
              <p style={{ color: '#c05621', marginBottom: '0.75rem' }}>{q.error}</p>
              <button onClick={retry} data-testid="audiobook-retry-btn" style={{ background: '#2f855a', color: 'white', border: 'none', borderRadius: 8, padding: '0.6rem 1.2rem', fontWeight: 700, cursor: 'pointer' }}>Retomar geração</button>
            </div>
          ) : st === 'ready' ? (
            <div data-testid="audiobook-ready" style={{ textAlign: 'center' }}>
              <p style={{ fontWeight: 600, color: '#2f855a', marginBottom: '0.5rem' }}>
                {q.enabled === false ? 'Audiolivro pronto, mas oculto no catálogo.' : 'Audiolivro pronto! Os assinantes já podem ouvir pelo catálogo.'}
              </p>
              <p style={{ fontSize: '0.82rem', color: '#4a5568', marginBottom: '0.75rem' }}>Ouça o áudio completo, marque o trecho que os assinantes devem ouvir (removendo páginas iniciais e finais) ou oculte o áudio do catálogo.</p>
              <button onClick={() => setListen(true)} data-testid="audiobook-listen-btn" style={{ background: '#0d9488', color: 'white', border: 'none', borderRadius: 8, padding: '0.6rem 1.2rem', fontWeight: 700, cursor: 'pointer' }}>Ouvir e editar trecho</button>
            </div>
          ) : null}
      </div>
      {listen && <AudioPlayerModal book={book} token={token} full onClose={() => setListen(false)}
        extra={(p) => <AudioTrimEditor book={book} token={token} initial={q} {...p} onSaved={load} />} />}
    </div>
  );
};
